import axios from "axios";
import { Document } from "../interfaces/document";

export function getDocuments(folder_id: number | null = null, user_id: number | null = null) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    let endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document';

    const params: string[] = [];

    // Always send folder_id parameter, even when null
    if (folder_id !== null) {
        params.push('folder_id=' + folder_id);
    } else {
        params.push('folder_id=null');
    }

    // Add user_id for admin viewing other user's documents
    if (user_id !== null) {
        params.push('user_id=' + user_id);
    }

    if (params.length > 0) {
        endpointUrl += '?' + params.join('&');
    }

    return axios.get(endpointUrl, { headers: headers });
}

export function getDocument(id: number) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id;
    return axios.get(endpointUrl, { headers: headers });
}

export function uploadDocument(formData: FormData) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token
        // No incluir Content-Type para que el navegador lo configure automáticamente con el boundary
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/upload';
    return axios.post(endpointUrl, formData, { headers: headers });
}

export function bulkUploadDocuments(formData: FormData) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token
        // No incluir Content-Type para que el navegador lo configure automáticamente con el boundary
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/bulk-upload';
    return axios.post(endpointUrl, formData, { headers: headers });
}

export function updateDocument(body_obj: { id: number, name?: string, description?: string, folder_id?: number | null }) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document';
    return axios.patch(endpointUrl, JSON.stringify(body_obj), { headers: headers });
}

export function deleteDocument(id: number) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id;
    return axios.delete(endpointUrl, { headers: headers });
}

export function downloadDocument(id: number) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id + '/download';
    return axios.get(endpointUrl, { headers: headers });
}

/**
 * Download a document as a blob (for protected documents)
 * If it's an image, opens in a new window. Otherwise, downloads directly.
 */
export async function downloadDocumentAsBlob(id: number, fileName: string, mimeType?: string): Promise<void> {
    const token = localStorage.getItem('token');
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id + '/download';

    const response = await axios.get(endpointUrl, {
        headers: {
            'Authorization': 'Bearer ' + token
        },
        responseType: 'blob'
    });

    const url = window.URL.createObjectURL(response.data);

    // Check if it's an image - open in new window
    const isImage = mimeType?.startsWith('image/') ||
                    /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i.test(fileName);

    if (isImage) {
        const newWindow = window.open(url, '_blank');
        // Revoke after a delay to allow the window to load
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } else {
        // Download directly
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }
}

/**
 * Get document blob URL for inline viewing (PDFs, images)
 */
export async function getDocumentBlobUrl(id: number): Promise<string> {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token
    };
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id + '/download?inline=1';

    const response = await axios.get(endpointUrl, {
        headers: headers,
        responseType: 'blob'
    });

    const blob = new Blob([response.data]);
    return URL.createObjectURL(blob);
}

export function shareDocument(id: number, body_obj: {
    share_type: string,
    share_target_id: number,
    share_role?: string | null,
    permission: string
}) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id + '/share';
    return axios.post(endpointUrl, JSON.stringify(body_obj), { headers: headers });
}

export function unshareDocument(id: number, share_id?: number) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/' + id + '/unshare';
    const body_obj = share_id ? { share_id: share_id } : {};
    return axios.post(endpointUrl, JSON.stringify(body_obj), { headers: headers });
}

export function bulkMoveDocument(document_ids: number[], folder_id: number | null) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/bulk-move';
    const body_obj = {
        document_ids: document_ids,
        folder_id: folder_id
    };
    return axios.post(endpointUrl, JSON.stringify(body_obj), { headers: headers });
}

export function bulkDeleteDocument(document_ids: number[]) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/bulk-delete';
    const body_obj = {
        document_ids: document_ids
    };
    return axios.post(endpointUrl, JSON.stringify(body_obj), { headers: headers });
}

export function bulkDownload(folder_ids: number[], document_ids: number[]) {
    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
    }
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/download/bulk';
    const body_obj = {
        folder_ids: folder_ids,
        document_ids: document_ids
    };
    return axios.post(endpointUrl, JSON.stringify(body_obj), { headers: headers });
}

/**
 * Bulk download documents/folders as ZIP (for protected documents)
 */
export async function bulkDownloadAsBlob(folder_ids: number[], document_ids: number[]): Promise<void> {
    const token = localStorage.getItem('token');
    const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/document/bulk-download';

    const response = await axios.post(endpointUrl, {
        document_ids: document_ids,
        folder_ids: folder_ids
    }, {
        headers: {
            'Authorization': 'Bearer ' + token
        },
        responseType: 'blob'
    });

    // Check if response is actually a JSON error
    if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.message || 'Error downloading documents');
    }

    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'documentos.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}
